import { Link } from 'react-router-dom';
import { Facebook, Instagram, Twitter, Linkedin, Phone, Mail, MapPin } from 'lucide-react';
import { useTenant } from '@/hooks/useTenant';
import { useLanguage } from '@/hooks/useLanguage';

const WhatsAppIcon = ({ className = '' }: { className?: string }) => (
 <svg
 viewBox="0 0 32 32"
 xmlns="http://www.w3.org/2000/svg"
 className={className}
 aria-hidden="true"
 >
 <path
 fill="currentColor"
 d="M16.003 3.2C8.934 3.2 3.2 8.934 3.2 16.003c0 2.258.595 4.464 1.724 6.407L3.2 28.8l6.537-1.71a12.78 12.78 0 0 0 6.266 1.617h.005c7.068 0 12.803-5.735 12.803-12.803 0-3.42-1.331-6.634-3.75-9.053a12.72 12.72 0 0 0-9.058-3.75Zm0 23.438h-.004a10.62 10.62 0 0 1-5.414-1.483l-.388-.23-4.04 1.058 1.078-3.938-.253-.404a10.61 10.61 0 0 1-1.62-5.638c0-5.868 4.775-10.643 10.645-10.643 2.842 0 5.513 1.108 7.523 3.12a10.57 10.57 0 0 1 3.117 7.528c-.003 5.87-4.776 10.63-10.644 10.63Zm5.836-7.97c-.32-.16-1.894-.935-2.188-1.042-.294-.107-.508-.16-.722.16-.213.32-.828 1.042-1.015 1.256-.187.214-.374.24-.694.08-.32-.16-1.352-.498-2.575-1.588-.952-.85-1.594-1.9-1.782-2.22-.187-.32-.02-.493.14-.652.143-.143.32-.374.48-.562.16-.187.213-.32.32-.534.107-.214.053-.4-.027-.562-.08-.16-.722-1.74-.99-2.385-.26-.625-.524-.54-.722-.55-.187-.008-.4-.01-.614-.01a1.18 1.18 0 0 0-.855.4c-.294.32-1.122 1.096-1.122 2.676 0 1.58 1.149 3.104 1.309 3.318.16.213 2.262 3.457 5.48 4.845.766.33 1.363.527 1.828.675.768.244 1.467.21 2.02.128.617-.092 1.894-.774 2.162-1.522.267-.748.267-1.388.187-1.522-.08-.134-.293-.213-.614-.374Z"
 />
 </svg>
);

export function Footer() {
 const { config } = useTenant();
 const { t } = useLanguage();

 if (!config) return null;

 const currentYear = new Date().getFullYear();

 const socialIcons = {
 facebook: Facebook,
 instagram: Instagram,
 twitter: Twitter,
 linkedin: Linkedin,
 };

 const quickLinks = [
 { name: t('nav.sales'), href: '/ventas' },
 { name: t('nav.rentals'), href: '/alquiler' },
 { name: t('nav.services'), href: '/servicios' },
 { name: t('nav.about'), href: '/quienes-somos' },
 { name: t('nav.blog'), href: '/blog' },
 { name: t('nav.contact'), href: '/contacto' },
 { name: t('nav.sellWithUs', 'Vende con nosotros'), href: '/vender' },
 ];

 return (
 <footer className="bg-foreground text-background">
 <div className="container mx-auto px-4 py-12">
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
 {/* Company Info */}
 <div className="lg:col-span-2">
 <div className="flex items-center space-x-2 mb-4">
 {config.logo ? (
 <img src={config.logo} alt={config.name} className="h-8 w-auto" />
 ) : (
 <div className="w-8 h-8 rounded-sm flex items-center justify-center bg-primary text-primary-foreground font-semibold text-base">
 {config.name.charAt(0)}
 </div>
 )}
 <span className="text-lg font-semibold tracking-tight">
 {config.name}
 </span>
 </div>
 <p className="text-muted-foreground/70 mb-6 max-w-md">
 {t('footer.description')}
 </p>

 {/* Contact Info */}
 <div className="space-y-3">
 {config.contact.address && (
 <div className="flex items-center gap-3">
 <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-400">
 <MapPin className="h-4 w-4" />
 </span>
 <span className="text-background/80">{config.contact.address}</span>
 </div>
 )}
 {config.contact.phone && (
 <a href={`tel:${config.contact.phone}`} className="flex items-center gap-3 group">
 <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-500/20 text-blue-300 group-hover:bg-blue-500/30 transition-colors">
 <Phone className="h-4 w-4" />
 </span>
 <span className="text-background/80 group-hover:text-background transition-colors">{config.contact.phone}</span>
 </a>
 )}
 {config.contact.email && (
 <a href={`mailto:${config.contact.email}`} className="flex items-center gap-3 group">
 <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-500/20 text-red-300 group-hover:bg-red-500/30 transition-colors">
 <Mail className="h-4 w-4" />
 </span>
 <span className="text-background/80 group-hover:text-background transition-colors break-all">{config.contact.email}</span>
 </a>
 )}
 {config.contact.whatsapp && (
 <a
 href={`https://wa.me/${String(config.contact.whatsapp).replace(/[^0-9]/g, '')}`}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center gap-3 group"
 >
 <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white">
 <WhatsAppIcon className="h-5 w-5" />
 </span>
 <span className="text-background/80 group-hover:text-background transition-colors">{config.contact.whatsapp}</span>
 </a>
 )}
 </div>
 </div>

 {/* Quick Links */}
 <div>
 <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
 <ul className="space-y-2">
 {quickLinks.map((link) => (
 <li key={link.name}>
 <Link
 to={link.href}
 className="text-background/60 hover:text-background transition-colors"
 >
 {link.name}
 </Link>
 </li>
 ))}
 </ul>
 </div>

 {/* Social Media */}
 <div>
 <h3 className="text-lg font-semibold mb-4">{t('footer.follow')}</h3>
 <div className="flex space-x-4">
 {Object.entries(config.social).map(([platform, url]) => {
 if (!url) return null;
 const IconComponent = socialIcons[platform as keyof typeof socialIcons];
 if (!IconComponent) return null;

 return (
 <a
 key={platform}
 href={url}
 target="_blank"
 rel="noopener noreferrer"
 className="w-10 h-10 rounded-full bg-background/10 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
 >
 <IconComponent className="h-5 w-5" />
 </a>
 );
 })}
 </div>
 </div>
 </div>

 {/* Bottom Bar */}
 <div className="border-t border-background/10 mt-8 pt-8 text-center">
 <p className="text-muted-foreground/70">
 © {currentYear} {config.name}. {t('footer.rights')}.
 </p>
 </div>
 </div>
 </footer>
 );
}